import { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Search, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { apiRequest } from '@/lib/queryClient';
import { useDebounce } from '@/hooks/use-debounce';
import type { GifData } from './hooks/use-league-chat';

// Re-export GifData type for convenience
export type { GifData } from './hooks/use-league-chat';

interface GifPickerProps {
  onSelect: (gif: GifData) => void;
  disabled?: boolean;
}

interface GiphyGif {
  giphyId: string;
  title: string;
  url: string;
  previewUrl: string;
  width: number;
  height: number;
}

interface GiphyResponse {
  gifs: GiphyGif[];
  pagination: {
    total_count: number;
    count: number;
    offset: number;
  };
}

export function GifPicker({ onSelect, disabled }: GifPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGif, setSelectedGif] = useState<GiphyGif | null>(null);

  const debouncedSearch = useDebounce(search, 300);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fetch trending GIFs on open
  useEffect(() => {
    if (open && gifs.length === 0 && !search) {
      fetchTrending();
    }
  }, [open]);

  // Search GIFs when search term changes
  useEffect(() => {
    if (debouncedSearch) {
      searchGifs(debouncedSearch);
    } else if (open) {
      fetchTrending();
    }
  }, [debouncedSearch]);

  const fetchTrending = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiRequest<GiphyResponse>('/api/chat/gifs/trending?limit=20');
      setGifs(response.gifs);
    } catch (err) {
      console.error('Failed to fetch trending GIFs:', err);
      setError('Failed to load GIFs');
    } finally {
      setIsLoading(false);
    }
  };

  const searchGifs = async (query: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiRequest<GiphyResponse>(`/api/chat/gifs/search?q=${encodeURIComponent(query)}&limit=20`);
      setGifs(response.gifs);
    } catch (err) {
      console.error('Failed to search GIFs:', err);
      setError('Failed to search GIFs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGifClick = (gif: GiphyGif) => {
    if (selectedGif?.giphyId === gif.giphyId) {
      // Second tap - send the GIF
      onSelect({
        giphyId: gif.giphyId,
        url: gif.url,
        previewUrl: gif.previewUrl,
        width: gif.width,
        height: gif.height,
      });
      setOpen(false);
      setSelectedGif(null);
      setSearch('');
    } else {
      // First tap - preview
      setSelectedGif(gif);
    }
  };

  const handleSendSelected = () => {
    if (selectedGif) {
      onSelect({
        giphyId: selectedGif.giphyId,
        url: selectedGif.url,
        previewUrl: selectedGif.previewUrl,
        width: selectedGif.width,
        height: selectedGif.height,
      });
      setOpen(false);
      setSelectedGif(null);
      setSearch('');
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSelectedGif(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5 border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 font-bold"
          disabled={disabled}
        >
          <span className="text-sm font-bold">GIF</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="top">
        <div className="flex flex-col max-h-[400px] overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search GIFs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-9"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Selected GIF preview */}
          {selectedGif && (
            <div className="p-3 border-b bg-muted/50">
              <div className="flex items-center gap-2">
                <img
                  src={selectedGif.previewUrl}
                  alt={selectedGif.title}
                  className="h-16 w-auto rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{selectedGif.title || 'GIF'}</p>
                  <p className="text-xs text-muted-foreground">Tap again or click Send</p>
                </div>
                <Button size="sm" onClick={handleSendSelected}>
                  Send
                </Button>
              </div>
            </div>
          )}

          {/* GIF grid */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto p-2 overscroll-contain"
            style={{
              maxHeight: selectedGif ? '200px' : '280px',
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              pointerEvents: 'auto',
            }}
            onWheel={(e) => {
              // Manually handle wheel events to ensure scrolling works
              e.stopPropagation();
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop += e.deltaY;
              }
            }}
          >
            {isLoading && gifs.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {error}
              </div>
            ) : gifs.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                {search ? 'No GIFs found' : 'No trending GIFs'}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {gifs.map((gif) => (
                  <button
                    key={gif.giphyId}
                    onClick={() => handleGifClick(gif)}
                    className={`relative aspect-square overflow-hidden rounded-lg hover:ring-2 hover:ring-primary transition-all ${
                      selectedGif?.giphyId === gif.giphyId ? 'ring-2 ring-primary' : ''
                    }`}
                  >
                    <img
                      src={gif.previewUrl}
                      alt={gif.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Giphy attribution (required) */}
          <div className="p-2 border-t flex items-center justify-center">
            <a
              href="https://giphy.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Powered by
              <img
                src="https://giphy.com/static/img/giphy_logo_square_social.png"
                alt="GIPHY"
                className="h-4"
              />
              GIPHY
            </a>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
